import { createContext, useContext } from 'react';
import type { LicenseStatus } from '../../core';

/**
 * Pro-license state shared across the app (spec §7.5). Pro UI elements stay
 * visible but locked until a valid key is active; this context is the single
 * source of truth for `isPro`. Kept component-free so the provider file can
 * satisfy react-refresh's components-only rule.
 */
export interface LicenseValue {
  /** Whether a valid Pro key is currently active. */
  isPro: boolean;
  /** Status of the stored key (invalid if none). */
  status: LicenseStatus;
  /**
   * Validate and, on success, persist a key. Real (remote-validated) keys make
   * this async, so callers await the result; stub/DEV keys still resolve
   * immediately. Returns the validation result.
   */
  activate: (key: string) => Promise<LicenseStatus>;
  /** Forget the stored key, returning to the free tier. */
  deactivate: () => void;
}

/**
 * Stable DOM id of the rail License panel's key input. Inline upgrade prompts
 * direct focus here instead of embedding a duplicate activation form.
 */
export const LICENSE_KEY_INPUT_ID = 'cf-license-key-input';

export const LicenseContext = createContext<LicenseValue | null>(null);

/** Access Pro-license state. Must be used within a LicenseProvider. */
export function useLicense(): LicenseValue {
  const ctx = useContext(LicenseContext);
  if (!ctx) throw new Error('useLicense must be used within a LicenseProvider');
  return ctx;
}
